import { useEffect, useState } from "react";
import { supabase } from "../supabase/supabaseClient";
import { useUser } from "../contexts/UserContext";
import { useNavigate, Link } from "react-router-dom";

const TABS = [
  { key: "all", label: "All" },
  { key: "open-call", label: "Open Calls" },
  { key: "residency", label: "Residencies" },
  { key: "international", label: "International" },
  { key: "study-abroad", label: "Study Abroad" },
];

// 🔹 Supabase opportunities 테이블 타입 정의
interface Opportunity {
  id: number;
  title: string;
  category: string;
  host: string | null;
  region: string | null;
  deadline: string | null;
  url: string | null;
  description: string | null;
}

export default function OpportunitiesPage() {
  const { user } = useUser();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // 🔹 여기서 제네릭으로 타입 지정
  const [items, setItems] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const pageSize = 10;
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // =============================
  // Fetch
  // =============================
  async function fetchData() {
    setLoading(true);

    // 전체 개수
    const { count } = await supabase
      .from("opportunities")
      .select("*", { count: "exact", head: true });

    if (typeof count === "number") {
      setTotalCount(count);
    }

    const offset = (page - 1) * pageSize;

    const { data, error } = await supabase
      .from("opportunities")
      .select("*")
      .order("deadline", { ascending: sortOrder === "asc" })
      .range(offset, offset + pageSize - 1);

    if (!error && data) {
      // 🔹 any[] -> Opportunity[] 로 자연스럽게 들어감
      setItems(data as Opportunity[]);
    } else {
      setItems([]);
    }

    setLoading(false);
  }

  useEffect(() => {
    fetchData();
  }, [page, sortOrder]);

  // =============================
  // Filtering (클라이언트 필터)
  // =============================
  const filteredItems = items.filter((item) => {
    // 탭 필터
    if (activeTab !== "all" && item.category !== activeTab) return false;

    // 검색(제목 + host)
    const keyword = search.toLowerCase();
    if (
      search &&
      !(
        item.title.toLowerCase().includes(keyword) ||
        (item.host || "").toLowerCase().includes(keyword)
      )
    ) {
      return false;
    }

    // 지역 필터
    if (regionFilter && item.region !== regionFilter) return false;

    return true;
  });

  const totalPages = Math.ceil(totalCount / pageSize);

  // 페이지 번호 범위 (현재 기준 앞뒤 2개)
  const startPage = Math.max(1, page - 2);
  const endPage = Math.min(totalPages, page + 2);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Title */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Opportunities</h1>

        {user?.userType === "admin" && (
          <Link
            to="/opportunities/new"
            className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
          >
            + Add
          </Link>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setActiveTab(t.key);
              setPage(1);
            }}
            className={`px-4 py-2 rounded-full border whitespace-nowrap ${
              activeTab === t.key
                ? "bg-black text-white"
                : "bg-white text-gray-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-2 mb-6">
        <input
          type="text"
          placeholder="Search title or host…"
          className="flex-1 border px-3 py-2 rounded"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        <select
          className="border px-3 py-2 rounded"
          value={regionFilter}
          onChange={(e) => {
            setRegionFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Regions</option>
          <option value="KR">Korea</option>
          <option value="US">USA</option>
          <option value="EU">Europe</option>
          <option value="JP">Japan</option>
          <option value="Other">Other</option>
        </select>

        <select
          className="border px-3 py-2 rounded"
          value={sortOrder}
          onChange={(e) => {
            setSortOrder(e.target.value);
            setPage(1);
          }}
        >
          <option value="asc">Deadline ↑</option>
          <option value="desc">Deadline ↓</option>
        </select>
      </div>

      {/* Loading */}
      {loading && (
        <div className="py-16 text-center text-gray-400">Loading…</div>
      )}

      {/* No Data */}
      {!loading && filteredItems.length === 0 && (
        <div className="py-16 text-center text-gray-400">
          No opportunities found.
        </div>
      )}

      {/* List */}
      <div className="flex flex-col gap-4">
        {filteredItems.map((item) => (
          <div
            key={item.id}
            onClick={() => navigate(`/opportunities/${item.id}`)}
            className="border rounded-xl p-5 bg-white shadow-sm cursor-pointer hover:bg-gray-50 flex flex-col gap-2"
          >
            <span className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full w-max">
              {TABS.find((t) => t.key === item.category)?.label}
            </span>

            <div className="text-lg font-semibold">{item.title}</div>

            <div className="text-sm text-gray-600">
              {item.host || "Unknown Host"}
              {item.region ? ` · ${item.region}` : ""}
            </div>

            {item.deadline && (
              <div className="text-sm font-medium">
                Deadline:{" "}
                <span className="font-semibold">
                  {new Date(item.deadline).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-10 text-sm select-none">
          {/* First */}
          <button
            onClick={() => setPage(1)}
            disabled={page === 1}
            className={`px-2 py-1 rounded ${
              page === 1 ? "text-gray-400" : "hover:bg-gray-100"
            }`}
          >
            «
          </button>

          {/* Prev */}
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={`px-2 py-1 rounded ${
              page === 1 ? "text-gray-400" : "hover:bg-gray-100"
            }`}
          >
            ‹
          </button>

          {/* Page Numbers (short range) */}
          {Array.from(
            { length: Math.max(0, endPage - startPage + 1) },
            (_, i) => startPage + i
          ).map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`px-3 py-1 rounded ${
                page === p
                  ? "bg-black text-white"
                  : "hover:bg-gray-100 text-gray-700"
              }`}
            >
              {p}
            </button>
          ))}

          {/* Next */}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded ${
              page === totalPages ? "text-gray-400" : "hover:bg-gray-100"
            }`}
          >
            ›
          </button>

          {/* Last */}
          <button
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded ${
              page === totalPages ? "text-gray-400" : "hover:bg-gray-100"
            }`}
          >
            »
          </button>
        </div>
      )}
    </div>
  );
}
